import { Text, View } from '@react-pdf/renderer';

import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles } from '../styles.ts';
import { MethodologyPageProps } from '../types.ts';

// ─── Shared extraordinary assumption items (identical across all variants) ────

const eaPlatformData = (
  <Text key="platform-data" style={fontStyles.body}>
    Market and cost data obtained through the Appraisify platform are reliable and reflective of
    prevailing conditions as of the effective date. If these data contain material errors, the value
    opinion may be affected.
  </Text>
);

const eaSowComprehensive = (
  <Text key="sow-comprehensive" style={fontStyles.body}>
    The client-provided Scope of Work is comprehensive and accurate. The costs associated with the
    SOW are assumed to be reasonable and reflective of the current market for labor and materials.
    If the SOW is incomplete or inaccurate, the value opinion may be affected.
  </Text>
);

const eaRenovationWorkmanlike = (
  <Text key="renovation-workmanlike" style={fontStyles.body}>
    The renovation work will be completed in a workmanlike manner and will meet all local building
    codes and zoning requirements. If the work is not completed as described, the value opinion may
    be affected.
  </Text>
);

export const MethodologyPage = ({
  subjectAddress,
  hasArvOption,
  inspectionData,
}: MethodologyPageProps) => {
  const isArv = hasArvOption === true;
  const isHybrid = inspectionData?.hasInspection === true;

  // ─── About This Report ──────────────────────────────────────────────────────

  // Paragraph 2: platform description
  let platformParagraph: JSX.Element;
  if (isArv && isHybrid) {
    platformParagraph = (
      <Text style={fontStyles.bodySpaced}>
        The appraisal was developed through the Appraisify Hybrid Platform for the purpose of asset
        valuation involving an investment lending product. The report utilizes third-party
        inspection data from Inspectify, Inc. regarding the subject&apos;s current &quot;As-Is&quot;
        condition, alongside a client-provided Scope of Work (SOW) detailing proposed renovations to
        derive an After-Repair Value (ARV).
      </Text>
    );
  } else if (isArv) {
    platformParagraph = (
      <Text style={fontStyles.bodySpaced}>
        This is a Desktop Appraisal developed through the Appraisify Platform for the purpose of
        asset valuation involving an investment lending product. The appraiser did not perform a
        personal interior or exterior inspection, nor was a third-party physical inspection report
        utilized.
      </Text>
    );
  } else if (isHybrid) {
    platformParagraph = (
      <Text style={fontStyles.bodySpaced}>
        The appraisal was developed through the Appraisify Hybrid Platform, which provides the
        appraiser with third-party inspection data from Inspectify, Inc., including floor plans and
        photographs, along with proprietary analytics.
      </Text>
    );
  } else {
    platformParagraph = (
      <Text style={fontStyles.bodySpaced}>
        The appraisal was developed through the Appraisify Desktop Platform, which provides the
        appraiser with data aggregations including MLS data, public records, satellite imagery, and
        proprietary analytics.
      </Text>
    );
  }

  // Paragraph 3: inspection statement (Desktop ARV covers this inside platformParagraph above)
  let inspectionParagraph: JSX.Element;
  if (isArv && !isHybrid) {
    inspectionParagraph = (
      <Text style={fontStyles.bodySpaced}>
        The valuation relies on digital data sources, public records, MLS data, and a
        client-provided Scope of Work (SOW) detailing proposed renovations to derive an After-Repair
        Value (ARV).
      </Text>
    );
  } else if (isHybrid) {
    inspectionParagraph = (
      <Text style={fontStyles.bodySpaced}>
        The appraiser did not perform a personal interior inspection. All physical characteristics,
        condition data, and{isArv ? ' photographs of the current state were' : ' photos were'}{' '}
        obtained from a{isArv ? '' : ' third-party'} property data report provided by Inspectify,
        Inc.
      </Text>
    );
  } else {
    // Desktop / AIV
    inspectionParagraph = (
      <Text style={fontStyles.bodySpaced}>
        The appraiser did not perform a personal inspection, nor was a third-party physical
        inspection report provided. All physical characteristics, condition data, and photographs
        were obtained from available digital data sources including MLS listings, tax records,
        satellite imagery, and virtual street views.
      </Text>
    );
  }

  // Paragraph 4: methods summary
  const methodsParagraph = isArv ? (
    <Text style={fontStyles.bodySpaced}>
      The Sales Comparison Approach was developed as the primary method, with comparable sales
      selected based on similarity to the subject&apos;s hypothetical post-renovation condition.
      When developed, the Cost Approach used data from the Craftsman National Building Cost Guide,
      inclusive of proposed renovation costs. The Income Approach was not developed unless
      specifically requested. Time adjustments were derived from Zillow HVI zip-code-level indices.
    </Text>
  ) : (
    <Text style={fontStyles.bodySpaced}>
      The Sales Comparison Approach was developed as the primary method. When developed, the Cost
      Approach used data from the Craftsman National Building Cost Guide. The Income Approach was
      not developed. Time adjustments were derived from Zillow HVI zip-code-level indices.
    </Text>
  );

  // ─── Scope of Work items ────────────────────────────────────────────────────

  let inspectionScopeItem: JSX.Element;
  if (isHybrid) {
    inspectionScopeItem = (
      <Text key="inspection" style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>Inspection.&nbsp;</Text>
        The appraiser relied on a{isArv ? '' : ' third-party'} property data report from Inspectify,
        Inc., including photographs and a sketch
        {isArv ? ', to establish the \u201cAs-Is\u201d condition' : ''}. No personal inspection was
        performed.
      </Text>
    );
  } else if (isArv) {
    inspectionScopeItem = (
      <Text key="inspection" style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>Inspection.&nbsp;</Text>
        No personal or third-party physical inspection was performed. The appraiser identified the
        subject&apos;s physical characteristics through digital data sources including MLS listings,
        tax records, satellite imagery, and prior appraisal data where available.
      </Text>
    );
  } else {
    inspectionScopeItem = (
      <Text key="inspection" style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>Inspection.&nbsp;</Text>
        No personal or third-party physical inspection was performed. The appraiser identified the
        subject&apos;s physical characteristics through digital data sources.
      </Text>
    );
  }

  let dataSourcesText: string;
  if (isArv && isHybrid) {
    dataSourcesText =
      'MLS, public records, tax assessor data, satellite imagery, Appraisify platform analytics, and the client-provided renovation SOW/budget.';
  } else if (isArv) {
    dataSourcesText =
      'MLS, public records, tax assessor data, satellite imagery, virtual street views, data aggregators, Appraisify platform analytics, and the client-provided renovation SOW/budget.';
  } else if (isHybrid) {
    dataSourcesText =
      'MLS, public records, tax assessor data, satellite imagery, and Appraisify platform analytics.';
  } else {
    dataSourcesText =
      'MLS, public records, tax assessor data, satellite imagery, virtual street views, data aggregators, and Appraisify platform analytics.';
  }

  const dataSourcesScopeItem = (
    <Text key="data-sources" style={fontStyles.body}>
      <Text style={fontStyles.bodyBold}>Data Sources.&nbsp;</Text>
      {dataSourcesText}
    </Text>
  );

  const salesComparisonScopeItem = (
    <Text key="sales-comparison" style={fontStyles.body}>
      <Text style={fontStyles.bodyBold}>Sales Comparison Approach.&nbsp;</Text>
      {isArv
        ? 'Comparable sales were selected based on similarity to the subject\u2019s hypothetical post-renovation condition, or adjusted accordingly. Adjustments for time were applied using Zillow HVI trend data. Location and physical adjustments were developed using recognized appraisal methods supported by market data. Condition and quality adjustments reflect the subject as if proposed renovations were complete.'
        : 'Comparable sales were selected based on proximity, recency, and physical similarity to the subject. Adjustments for time were applied using Zillow HVI trend data. Location and physical adjustments were developed using recognized appraisal methods supported by market data.'}
    </Text>
  );

  const costApproachScopeItem = (
    <Text key="cost-approach" style={fontStyles.body}>
      <Text style={fontStyles.bodyBold}>Cost Approach.&nbsp;</Text>
      {isArv
        ? 'When included, the Cost Approach was developed using Craftsman cost data, inclusive of the proposed renovation costs.'
        : 'When included, the Cost Approach was developed using Craftsman cost data and applied to all property types except condominiums.'}
    </Text>
  );

  const scopeOfWorkItems = [
    <Text key="property-identification" style={fontStyles.body}>
      <Text style={fontStyles.bodyBold}>Property Identification.&nbsp;</Text>
      The subject was identified by street address, parcel number, and GIS mapping tools
      {isArv ? '.' : ' verified through the Appraisify platform.'}
    </Text>,
    inspectionScopeItem,
    ...(isArv
      ? [
          <Text key="renovation-analysis" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>Renovation Analysis.&nbsp;</Text>
            The appraiser reviewed the client-provided Scope of Work and/or renovation budget. The
            analysis assumes these improvements will be completed in a workmanlike manner consistent
            with local market standards.
          </Text>,
        ]
      : []),
    dataSourcesScopeItem,
    salesComparisonScopeItem,
    costApproachScopeItem,
    <Text key="reconciliation" style={fontStyles.body}>
      <Text style={fontStyles.bodyBold}>Reconciliation.&nbsp;</Text>
      The appraiser weighed the approaches developed and reconciled to a final opinion of value
      supported by the most reliable indicators.
    </Text>,
  ];

  // ─── Assumptions and Limiting Conditions items ──────────────────────────────

  const sketchesAssumption = (
    <Text key="sketches" style={fontStyles.body}>
      Any sketch or floor plan included illustrates approximate dimensions
      {!isHybrid
        ? ' based on public record or prior listing data and is provided only to assist the reader in visualizing the property.'
        : ' and is provided only to assist the reader in visualizing the property.'}
    </Text>
  );

  let thirdPartyDataAssumption: JSX.Element;
  if (isHybrid && isArv) {
    thirdPartyDataAssumption = (
      <Text key="third-party-data" style={fontStyles.body}>
        The appraiser has relied on data provided by third parties, including the Inspectify, Inc.
        property data report and the client-provided Scope of Work. The appraiser used only data
        considered reliable but makes no guarantees regarding accuracy or completeness.
      </Text>
    );
  } else if (isHybrid) {
    thirdPartyDataAssumption = (
      <Text key="third-party-data" style={fontStyles.body}>
        The appraiser has relied on data provided by third parties, including but not limited to
        floor plans, MLS, tax records, public land records, satellite imagery, and the Inspectify,
        Inc. property data report. The appraiser used only data considered reliable but makes no
        guarantees regarding accuracy or completeness. If source data predates the effective date,
        property characteristics are assumed unchanged.
      </Text>
    );
  } else if (isArv) {
    thirdPartyDataAssumption = (
      <Text key="third-party-data" style={fontStyles.body}>
        The appraiser has relied on data provided by third-party aggregators and the client-provided
        Scope of Work, including but not limited to MLS listings, tax records, public land records,
        satellite imagery, virtual street views, and data services. The appraiser used only data
        considered reliable but makes no guarantees regarding accuracy or completeness. If source
        data predates the effective date, property characteristics and condition are assumed
        unchanged.
      </Text>
    );
  } else {
    thirdPartyDataAssumption = (
      <Text key="third-party-data" style={fontStyles.body}>
        The appraiser has relied on data provided by third-party aggregators, including but not
        limited to MLS listings, tax records, public land records, satellite imagery, virtual street
        views, and data services. The appraiser used only data considered reliable but makes no
        guarantees regarding accuracy or completeness. If source data (such as MLS photos or
        descriptions) predates the effective date, property characteristics and condition are
        assumed unchanged.
      </Text>
    );
  }

  const assumptionsItems = [
    <Text key="title" style={fontStyles.body}>
      The appraiser assumes that the title is good and marketable unless information to the contrary
      becomes known.
    </Text>,
    sketchesAssumption,
    thirdPartyDataAssumption,
    ...(isArv
      ? [
          <Text key="sow-reliance" style={fontStyles.body}>
            The appraiser has relied on the specific line items, descriptions, and materials
            outlined in the client-provided SOW. The appraiser is not a contractor or building
            inspector. It is assumed that the SOW is adequate to bring the property to the proposed
            condition and that all work will be professionally completed to code. The appraiser
            assumes no responsibility for hidden defects discovered during renovation that would
            alter the SOW or cost to cure.
          </Text>,
        ]
      : []),
    <Text key="hidden-defects" style={fontStyles.body}>
      Unless otherwise stated, the appraiser has no knowledge of hidden or unapparent physical
      deficiencies or adverse environmental conditions and assumes none exist. This report is not an
      environmental assessment.
    </Text>,
    <Text key="zoning" style={fontStyles.body}>
      It is assumed that the property is in compliance with all applicable zoning, use regulations,
      and building codes unless otherwise noted in this report.
    </Text>,
    <Text key="testimony" style={fontStyles.body}>
      The appraiser will not be required to give testimony or appear in court because of this
      appraisal unless specific arrangements have been made in advance or as required by law.
    </Text>,
  ];

  // ─── Extraordinary Assumptions items ────────────────────────────────────────

  let extraordinaryAssumptionsItems: JSX.Element[];
  if (isHybrid && isArv) {
    extraordinaryAssumptionsItems = [
      <Text key="physical-chars" style={fontStyles.body}>
        The physical characteristics and condition of the property (&quot;As-Is&quot;), as reported
        by the Inspectify, Inc. property data report, are accurate as of the effective date. If the
        actual condition differs materially from what was reported, the value opinion may be
        affected.
      </Text>,
      eaSowComprehensive,
      eaRenovationWorkmanlike,
      eaPlatformData,
    ];
  } else if (isHybrid) {
    // Hybrid / AIV
    extraordinaryAssumptionsItems = [
      <Text key="physical-chars" style={fontStyles.body}>
        The physical characteristics and condition of the property, as reported by the Inspectify,
        Inc. property data report, are accurate as of the effective date. If the actual condition
        differs materially from what was reported, the value opinion may be affected.
      </Text>,
      eaPlatformData,
    ];
  } else if (isArv) {
    // Desktop / ARV
    extraordinaryAssumptionsItems = [
      <Text key="physical-chars" style={fontStyles.body}>
        The physical characteristics, gross living area, and condition of the property, as derived
        from MLS, public records, and data aggregations, are accurate as of the effective date. If
        the actual physical characteristics differ significantly from those used in this analysis
        (e.g., unreported fire damage, gut renovation not reflected in records), the value opinion
        may be affected.
      </Text>,
      <Text key="conflicting-sources" style={fontStyles.body}>
        Where data sources displayed conflicting information regarding physical characteristics, the
        appraiser relied on the source deemed most credible and current. It is assumed that the
        selected data source is accurate. If it is not, the value opinion may be affected.
      </Text>,
      <Text key="condition" style={fontStyles.body}>
        The property is assumed to be structurally sound and in a condition consistent with
        available data sources, unless specific information indicates otherwise. If the actual
        condition differs materially, the value opinion may be affected.
      </Text>,
      eaSowComprehensive,
      eaRenovationWorkmanlike,
      eaPlatformData,
    ];
  } else {
    // Desktop / AIV
    extraordinaryAssumptionsItems = [
      <Text key="physical-chars" style={fontStyles.body}>
        The physical characteristics, gross living area, and condition of the property, as derived
        from MLS, public records, and data aggregations, are accurate as of the effective date. If
        the actual physical characteristics differ significantly from those used in this analysis,
        the value opinion may be affected.
      </Text>,
      <Text key="conflicting-sources" style={fontStyles.body}>
        Where data sources (e.g., MLS vs. public records) displayed conflicting information
        regarding physical characteristics, the appraiser relied on the source deemed most credible
        and current. It is assumed that the selected data source is accurate. If it is not, the
        value opinion may be affected.
      </Text>,
      <Text key="condition" style={fontStyles.body}>
        The property is assumed to be in a condition consistent with available data sources,
        including MLS photos, listing descriptions, and public records, unless specific information
        indicates otherwise. If the actual condition differs materially, the value opinion may be
        affected.
      </Text>,
      eaPlatformData,
    ];
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  const intendedUserSentence = (
    <Text style={fontStyles.bodySpaced}>
      The intended user is the client named in this report and its authorized assigns. Any use by
      unintended parties is not authorized.
    </Text>
  );

  const limitationOfLiability = (
    <View style={{ gap: 8 }}>
      <Text style={fontStyles.h4}>Limitation of Liability</Text>
      <Text style={fontStyles.bodySpaced}>
        This appraisal was prepared for the stated intended use and intended user only. Unauthorized
        use or alteration is not permitted. The appraiser shall have no liability for any use
        outside the stated intended purpose. Appraisify, as the appraisal management company,
        assumes no liability for unauthorized use or reliance by parties other than the intended
        user.
      </Text>
      <Text style={fontStyles.bodySpaced}>
        The appraiser and Appraisify will retain a copy of this report and all supporting data for
        the period required by USPAP and applicable state law.
      </Text>
    </View>
  );

  return (
    <>
      {/* Page 1: About This Report + Scope of Work + Definition of Market Value
                  (+ Intended Use and User for AIV only) */}
      <Tkv2PDFPage address={subjectAddress}>
        <Text style={fontStyles.h4}>About This Report</Text>
        <Text style={fontStyles.bodySpaced}>
          This appraisal has been prepared and reported in accordance with the Uniform Standards of
          Professional Appraisal Practice (USPAP), 2024 edition, under Standards Rule 2-2(b).
        </Text>
        {platformParagraph}
        {inspectionParagraph}
        {methodsParagraph}

        <Text style={fontStyles.h4}>Scope of Work</Text>
        <List type="number" items={scopeOfWorkItems} />

        <Text style={fontStyles.h4}>Definition of Market Value</Text>
        <Text style={fontStyles.bodySpaced}>
          The definition of Market Value conforms to 12 CFR Part 34, Subpart C, §34.42(g) of the
          Office of the Comptroller of the Currency:
        </Text>
        <Text style={fontStyles.bodySpaced}>
          &quot;The most probable price which a property should bring in a competitive and open
          market under all conditions requisite to a fair sale, the buyer and seller, each acting
          prudently, knowledgeably and assuming the price is not affected by undue stimulus.
          Implicit in this definition is the consummation of a sale as of a specified date and the
          passing of title from seller to buyer under conditions whereby: (1) buyer and seller are
          typically motivated; (2) both parties are well informed or well advised, and each acting
          in what he or she considers his or her own best interest; (3) a reasonable time is allowed
          for exposure in the open market; (4) payment is made in terms of cash in U.S. dollars or
          in terms of financial arrangements comparable thereto; and (5) the price represents the
          normal consideration for the property sold unaffected by special or creative financing or
          sales concessions granted by anyone associated with the sale.&quot;
        </Text>

        {!isArv && (
          <>
            <Text style={fontStyles.h4}>Intended Use and User</Text>
            <Text style={fontStyles.bodySpaced}>
              The intended use of this report is for collateral evaluation, risk management, and
              credit decisioning by the client.
            </Text>
            {intendedUserSentence}
          </>
        )}
      </Tkv2PDFPage>

      {/* Page 2: (ARV only) Important Note + Intended Use, then Assumptions + Assignment Conditions */}
      <Tkv2PDFPage address={subjectAddress}>
        {isArv && (
          <>
            <Text style={fontStyles.bodySpaced}>
              <Text style={fontStyles.bodyBold}>Important Note Regarding Value:&nbsp;</Text>
              The value opinion reported herein is an After-Repair Value (ARV). It represents the
              probable price the property should bring in a competitive market subject to the
              hypothetical condition that the renovations detailed in the client&apos;s SOW have
              been completed as of the effective date.
            </Text>
            <Text style={fontStyles.bodySpaced}>
              If an &quot;As-Is&quot; Value is also reported, it represents the value of the
              property in its current physical condition prior to the commencement of the proposed
              SOW.
            </Text>

            <Text style={fontStyles.h4}>Intended Use and User</Text>
            <Text style={fontStyles.bodySpaced}>
              The intended use of this report is for collateral evaluation, risk management, and
              credit decisioning for investment lending and renovation financing.
            </Text>
            {intendedUserSentence}
          </>
        )}

        <Text style={fontStyles.h4}>Assumptions and Limiting Conditions</Text>
        <List type="number" items={assumptionsItems} />

        <Text style={fontStyles.h4}>Assignment Conditions</Text>
        <Text style={fontStyles.bodySpaced}>
          <Text style={fontStyles.bodyBold}>Interest Appraised:&nbsp;</Text>
          Fee Simple Estate, unless otherwise stated.
        </Text>
        <Text style={[fontStyles.bodySpaced, { marginBottom: 4 }]}>
          <Text style={fontStyles.bodyBold}>Extraordinary Assumptions:&nbsp;</Text>
          The use of any extraordinary assumption may have affected the assignment results.
        </Text>
        <List type="number" items={extraordinaryAssumptionsItems} />

        {!isArv && limitationOfLiability}
      </Tkv2PDFPage>

      {/* Page 3: ARV only — Hypothetical Conditions + Limitation of Liability */}
      {isArv && (
        <Tkv2PDFPage address={subjectAddress}>
          <Text style={fontStyles.h4}>Hypothetical Conditions:</Text>
          <List
            type="number"
            items={[
              <Text key="arv-hypothetical" style={fontStyles.body}>
                The After-Repair Value (ARV) is based on the hypothetical condition that all
                repairs, renovations, and improvements detailed in the provided Scope of Work have
                been completed as of the effective date of the appraisal. The property does not
                currently exist in this state; the value is predicated on the future completion of
                these specific tasks. The use of this hypothetical condition may have affected the
                assignment results.
              </Text>,
            ]}
          />

          {limitationOfLiability}
        </Tkv2PDFPage>
      )}
    </>
  );
};
